import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse, ModuleKey, Permission } from '@tengesa/shared';
import { api, ApiError } from '../lib/api';

interface SessionValue {
  me: MeResponse;
  can(p: Permission): boolean;
  canAny(...p: Permission[]): boolean;
  hasModule(m: ModuleKey): boolean;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useMeQuery() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/api/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function SessionProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const qc = useQueryClient();
  const perms = new Set(me.user.permissions);
  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);
  return (
    <SessionContext.Provider
      value={{
        me,
        can: (p) => perms.has(p),
        canAny: (...ps) => ps.some((p) => perms.has(p)),
        hasModule: (m) => me.business.enabledModules.includes(m),
        refresh,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** Listens for 401/403 signals from the API client and re-checks the session. */
export function useSessionEvents() {
  const qc = useQueryClient();
  useEffect(() => {
    const signedOut = () => {
      qc.setQueryData(['me'], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    };
    const refresh = () => void qc.invalidateQueries({ queryKey: ['me'] });
    window.addEventListener('tengesa:signed-out', signedOut);
    window.addEventListener('tengesa:refresh-me', refresh);
    return () => {
      window.removeEventListener('tengesa:signed-out', signedOut);
      window.removeEventListener('tengesa:refresh-me', refresh);
    };
  }, [qc]);
}
